/**
 * FHNW edu-ID callback screen (#961).
 *
 * The upstream component places an absolute Semantic UI loader inside an
 * empty container. Render explicit progress instead, but keep upstream's
 * isLoading gate: without it the screen never leaves the "in progress"
 * state when no token and no error ever arrive.
 */
import React, { useEffect } from 'react';
import { oidcLogin } from '@plone-collective/volto-authomatic/actions';
import { Container, Loader } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { Toast } from '@plone/volto/components';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import eduidMessages from '@fhnw/messages/eduid';

const messages = defineMessages({
  oAuthLoginFailed: {
    id: 'Login Failed',
    defaultMessage: 'Login Failed',
  },
  oAuthLoginFailedContent: {
    id: 'Authentication failed.',
    defaultMessage: 'Authentication failed.',
  },
});

function LoginOIDC({ intl }) {
  const query = useLocation().search;
  const { provider } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const session = useSelector((state) => state.oidcRedirect.session);
  const userSession = useSelector((state) => state.userSession);
  const error = userSession.login.error;
  const token = userSession.token;
  const isLoading = userSession.login.loading;
  const [cookies, , removeCookie] = useCookies();
  const returnUrl = cookies.return_url || '/';

  useEffect(() => {
    dispatch(oidcLogin(provider, query, session));
  }, [dispatch, provider, query, session]);

  useEffect(() => {
    if (token) {
      window.setTimeout(() => removeCookie('return_url', { path: '/' }), 500);
      history.push(returnUrl);
      if (toast.isActive('loginFailed')) {
        toast.dismiss('loginFailed');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, history]);

  useEffect(() => {
    if (error) {
      history.push('/login');
      if (!toast.isActive('loginFailed')) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messages.oAuthLoginFailed)}
            content={intl.formatMessage(messages.oAuthLoginFailedContent)}
          />,
          { autoClose: false, toastId: 'loginFailed' },
        );
      }
    }
  }, [error, history, intl]);

  return (
    <div id="page-login" className="eduid-authenticating">
      <Container>
        {isLoading && (
          <div
            className="eduid-authenticating__status"
            role="status"
            aria-live="polite"
          >
            <Loader active inline="centered" size="large" aria-hidden="true" />
            <h1>
              <FormattedMessage {...eduidMessages.authenticating} />
            </h1>
            <p>
              <FormattedMessage {...eduidMessages.authenticatingHint} />
            </p>
          </div>
        )}
      </Container>
    </div>
  );
}

export default injectIntl(LoginOIDC);
